import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import './account.css';

function AccountPage({ onNavigateToJobs }) {
  const { user, token, login, logout } = useAuth();
  const initialTab = new URLSearchParams(window.location.search).get('tab') === 'register' ? 'register' : 'login';
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const update = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const endpoint = tab === 'login' ? '/api/auth/login' : '/api/auth/register';
    const body = tab === 'login'
      ? { email: form.email, password: form.password }
      : { username: form.username, email: form.email, password: form.password };
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong'); return; }
      login(data.token, data.user);
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const res = await fetch('/api/auth/account', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setDeleteError(d.error || 'Failed'); return; }
      logout();
    } catch {
      setDeleteError('Network error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const saveUsername = async () => {
    if (!newUsername.trim()) return;
    setUsernameError('');
    setUsernameLoading(true);
    try {
      const res = await fetch('/api/auth/username', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username: newUsername.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setUsernameError(data.error || 'Failed to update'); return; }
      login(data.token, data.user);
      setEditingUsername(false);
      setNewUsername('');
    } catch {
      setUsernameError('Network error');
    } finally {
      setUsernameLoading(false);
    }
  };

  if (user) {
    return (
      <div className="account-page">
        <div className="account-card">
          {editingUsername ? (
            <div className="account-username-edit">
              <input
                className="account-username-input"
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                placeholder={user.username}
                maxLength={30}
                autoFocus
              />
              {usernameError && <p className="account-error">{usernameError}</p>}
              <div className="account-username-edit-actions">
                <button className="account-edit-cancel" onClick={() => { setEditingUsername(false); setNewUsername(''); setUsernameError(''); }}>Cancel</button>
                <button className="account-btn-primary account-edit-save" onClick={saveUsername} disabled={usernameLoading || !newUsername.trim()}>
                  {usernameLoading ? '...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="account-username-row">
              <h2 className="account-username">{user.username}</h2>
              <button className="account-edit-link" onClick={() => { setEditingUsername(true); setNewUsername(user.username); }}>Edit</button>
            </div>
          )}
          <p className="account-email">{user.email}</p>
          <p className="account-since">Member since {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
          <div className="account-actions">
            <button className="account-btn-secondary" onClick={() => onNavigateToJobs({})}>Browse Jobs</button>
            <button className="account-btn-logout" onClick={logout}>Sign out</button>
          </div>

          <div className="account-danger-zone">
            {!confirmDelete ? (
              <button className="account-delete-link" onClick={() => setConfirmDelete(true)}>Delete account</button>
            ) : (
              <div className="account-delete-confirm">
                <p className="account-delete-warning">This will permanently delete your account and all your data. Are you sure?</p>
                {deleteError && <p className="account-error">{deleteError}</p>}
                <div className="account-delete-actions">
                  <button className="account-edit-cancel" onClick={() => { setConfirmDelete(false); setDeleteError(''); }}>Cancel</button>
                  <button className="account-delete-confirm-btn" onClick={deleteAccount} disabled={deleteLoading}>
                    {deleteLoading ? '...' : 'Yes, delete my account'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-card">
        <h2 className="account-card-title">ferl</h2>
        <p className="account-card-sub">jobs worth reading about</p>

        <div className="account-tabs">
          <button className={tab === 'login' ? 'active' : ''} onClick={() => { setTab('login'); setError(''); }}>Sign in</button>
          <button className={tab === 'register' ? 'active' : ''} onClick={() => { setTab('register'); setError(''); }}>Create account</button>
        </div>

        <a href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/auth/google`} className="account-btn-google">
          <svg className="account-google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </a>

        <div className="account-divider"><span>or</span></div>

        <form onSubmit={submit} className="account-form">
          {tab === 'register' && (
            <input name="username" placeholder="Username" value={form.username} onChange={update} required autoComplete="username" />
          )}
          <input name="email" type="email" placeholder="Email" value={form.email} onChange={update} required autoComplete="email" />
          <input name="password" type="password" placeholder="Password" value={form.password} onChange={update} required autoComplete={tab === 'login' ? 'current-password' : 'new-password'} />
          {error && <p className="account-error">{error}</p>}
          <button type="submit" className="account-btn-primary" disabled={loading}>
            {loading ? '...' : tab === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AccountPage;
